<template>
  <AutocompleteInput
    ref="autocomplete"
    :results="searchResults"
    :placeholder="placeholder || `Search for ${recordType || 'record'}...`"
    :size="size"
    :initial-value="initialDisplayValue"
    @search="handleSearch"
    @select="selectRecord"
  >
    <span v-if="hasSelectedRecord" class="icon clear r-1 is-small is-right" @click="selectRecord()">
      <font-awesome-icon icon="circle-xmark" />
    </span>
    <template #suggestion="{ result, index, isActive }">
      <slot
        name="suggestion"
        :record="getRecordById(result[0])"
        :index="index"
        :is-active="isActive"
      >
        <RecordElement
          v-if="getRecordById(result[0])"
          :value="getRecordById(result[0])!"
          :size="size"
          readonly
          fixed
        />
      </slot>
    </template>
    <template v-if="$slots.empty" #empty="{ query }">
      <slot name="empty" :query="query" />
    </template>
  </AutocompleteInput>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import type { SearchedRecord } from "@auger/core"
import AutocompleteInput from "@auger/ui/components/AutocompleteInput.vue"
import RecordElement from "@auger/ui/components/elements/RecordElement.vue"
import { client } from "@/init/apollo"
import { getAuger } from "@/init/auger"

interface Props {
  modelValue?: number | string | null | object
  recordType?: string
  placeholder?: string | null
  size?: string
  initialName?: string
  excludeIds?: number[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  recordType: undefined,
  placeholder: null,
  size: "normal",
  initialName: "",
  excludeIds: () => [],
})

const emit = defineEmits<{
  "update:modelValue": [value: number | null]
  selected: [record: SearchedRecord | null]
}>()

const autocomplete = ref<InstanceType<typeof AutocompleteInput> | null>(null)
const records = ref<SearchedRecord[]>([])
const displayValue = ref("")
const initialDisplayValue = ref("")

const searchResults = computed((): [number, string][] => {
  return records.value.map((record): [number, string] => [record.id, record.name])
})

const hasSelectedRecord = computed(() => {
  return props.modelValue !== null && props.modelValue !== undefined && props.modelValue !== ""
})

watch(
  () => props.modelValue,
  (newVal) => {
    if (!newVal) {
      displayValue.value = ""
      initialDisplayValue.value = ""
    }
  },
  { immediate: true }
)

watch(
  () => props.initialName,
  (newVal) => {
    if (newVal) {
      initialDisplayValue.value = newVal
      displayValue.value = newVal
    }
  },
  { immediate: true }
)

function handleSearch(query: string) {
  if (!query || query.length < 2) {
    records.value = []
    return
  }

  // recordType undefined → search across all record types (polymorphic links),
  // up to 100; a typed search takes the query's default of 20.
  const type = props.recordType
  getAuger(client)
    .records.search(`%${query}%`, { type, limit: type ? undefined : 100 })
    .then((found) => {
      records.value = found.filter((record) => !props.excludeIds.includes(record.id))
    })
    .catch((error: unknown) => {
      console.error(`Error searching ${props.recordType ?? "any"}:`, error)
      records.value = []
    })
}

function selectRecord(selection?: [number, string]) {
  if (selection) {
    const record = records.value.find((r) => r.id === selection?.[0])!
    displayValue.value = record.name
    emit("update:modelValue", record.id)
    emit("selected", record)
    records.value = []
  } else {
    displayValue.value = ""
    emit("update:modelValue", null)
    emit("selected", null)
  }
}

function clear() {
  if (autocomplete.value) {
    autocomplete.value.clear()
  }
  displayValue.value = ""
  records.value = []
  emit("update:modelValue", null)
}

function getRecordById(id: number) {
  return records.value.find((r) => r.id === id)
}

function focus() {
  autocomplete.value?.focus()
}

defineExpose({ clear, focus })
</script>
<style>
.autocomplete {
  .clear {
    z-index: 10000 !important;
    cursor: pointer;
    padding: 3px 3px 2px 7px;
    display: none;
    position: absolute;
    right: 1px;
    top: 1px;
    width: 27px;
    height: 24px !important;
    line-height: 21.6px !important;
    color: lightgray;

    &.is-small {
      width: 22px;
      height: 22px !important;
      padding: 0;
      line-height: 1em;
      top: 0px;
    }
  }

  &:hover {
    .clear {
      display: inline;
      color: lightgray;
    }
  }

  &:focus-within {
    .clear {
      display: inline;
      color: gray;
    }
  }
}
</style>
